import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Box, Paper } from '@mui/material';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';
import { ArrowLeft, Clock, Users, ChefHat, Edit, Trash2, Copy, Plus, List, AlertTriangle, Ban, Coffee, Lightbulb } from 'lucide-react';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { getCustomRecipeDetails, deleteCustomRecipe, copyCustomRecipe } from '@/lib/apiService';
import { CustomRecipeDetailsResponse } from '@/types/custom-recipe';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { COOKING_METHOD_OPTIONS } from '@/constants/dietary-goals';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import cup from '@/assets/images/cup.svg';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import NutritionMetricStatusCard from '@/components/shared/NutritionMetricStatusCard';
import { formatMacro } from '@/utils/gram-ml';
import { MACRO_CHART_COLORS } from '@/constants/macro-chart-colors';
import RecipeIngredientCards from '@/components/recipes/RecipeIngredientCards';
import { formatIngredientDisplayName } from '@/lib/utils';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';

const formatServingNumber = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const formatServingUnitName = (value: string, quantity: number): string => {
  const normalized = formatUnderscoreDisplayLabel(value).toLowerCase();
  if (!normalized) return '';
  if (quantity === 1 || normalized.endsWith('s')) return normalized;
  return `${normalized}s`;
};

const formatPieceQuantity = (quantity: number): string => {
  const pieceLabel = quantity === 1 ? 'piece' : 'pieces';
  return `${formatServingNumber(quantity)} ${pieceLabel}`;
};

const formatServingSizeLabel = (serving?: {
  label?: string | null;
  servingId?: string;
  grams?: number | null;
  quantity?: number | null;
}) => {
  if (!serving) return 'Serving';
  const label = formatUnderscoreDisplayLabel(serving.label || serving.servingId || 'Serving');
  const servingId = formatUnderscoreDisplayLabel(serving.servingId || '');
  const grams = Number(serving.grams);
  const quantity = Number(serving.quantity);
  const hasGrams = Number.isFinite(grams) && grams > 0;
  const hasPieceQuantity = Number.isFinite(quantity) && quantity > 0 && Math.abs(quantity - 1) >= 0.01;

  const servingName = formatServingUnitName(servingId && servingId !== label ? servingId : label, 1) || label.toLowerCase();
  const parts = [
    hasPieceQuantity ? formatPieceQuantity(quantity) : null,
    hasGrams ? `${formatServingNumber(grams)}g` : null,
  ].filter((part): part is string => Boolean(part));

  if (parts.length > 0) {
    return `1 ${servingName} (${parts.join(', ')})`;
  }

  return `1 ${servingName}`;
};

const RecipeDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [recipe, setRecipe] = useState<CustomRecipeDetailsResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  const fetchRecipeDetails = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getCustomRecipeDetails(id);
      
      if (response.success && response.data) {
        setRecipe(response.data);
      } else {
        setError(response.message || 'Recipe not found');
      }
    } catch (err: unknown) {
      console.error('Error fetching recipe details:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch recipe details');
      showErrorToast(err, "Error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      void fetchRecipeDetails();
    }
  }, [id, fetchRecipeDetails]);

  const handleDelete = async () => {
    if (!recipe || !id) return;
    const canMutateRecipe = Boolean(
      typeof recipe.isOwn === 'boolean'
        ? recipe.isOwn
        : (typeof recipe.canEdit === 'boolean' ? recipe.canEdit : false)
    );
    if (!canMutateRecipe) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete "${recipe.recipeName}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      const response = await deleteCustomRecipe(id);
      
      if (response.success) {
        navigate('/recipes');
      } else {
        throw new Error(response.message || 'Failed to delete recipe');
      }
    } catch (err: unknown) {
      console.error('Error deleting recipe:', err);
      showErrorToast(err, "Error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopy = async () => {
    if (!id || !recipe) return;

    try {
      setIsCopying(true);
      const response = await copyCustomRecipe(id, { isPublic: false });
      const copiedRecipeId = response.data?.id;

      showSuccessToast('Recipe Copied', 'Your copy is ready to edit.');

      if (!copiedRecipeId) {
        throw new Error('The copied recipe was created, but its ID was not returned.');
      }

      navigate(`/recipes/${copiedRecipeId}/edit`);
    } catch (err: unknown) {
      console.error('Error copying recipe:', err);
      showErrorToast(err, 'Copy Failed');
    } finally {
      setIsCopying(false);
    }
  };

  const handleAddToMealPlan = () => {
    navigate('/meal-planning');
  };

  const formatTime = (minutes: number) => {
    if (!minutes) return '0m';
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] p-6">
        <div className="max-w-6xl mx-auto space-y-8">
          <Skeleton className="h-8 w-40" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-96 w-full rounded-2xl" />
              <Skeleton className="h-64 w-full rounded-xl" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-48 w-full rounded-xl" />
              <Skeleton className="h-32 w-full rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-6">
        <Card className="max-w-md w-full text-center shadow-xl border-0">
          <CardContent className="py-16">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ChefHat className="h-10 w-10 text-red-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Recipe Not Found</h3>
            <p className="text-gray-600 mb-8">{error || 'The recipe you\'re looking for doesn\'t exist.'}</p>
            <Link to="/recipes">
              <Button size="lg" className="bg-[#2EB4E4] hover:bg-[#20AEDC] text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Recipes
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canEditRecipe = Boolean(
    typeof recipe.isOwn === 'boolean'
      ? recipe.isOwn
      : (typeof recipe.canEdit === 'boolean' ? recipe.canEdit : false)
  );
  const canCopyRecipe = recipe.canCopy !== false;
  const copyUnavailableReason = recipe.copyUnavailableReason
    || 'This legacy recipe cannot be duplicated because one or more ingredients are no longer linked to the ingredient catalog.';

  const ingredientsData = Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0
    ? recipe.ingredients.map((ingredient) => {
        const qty = Number((ingredient as any).quantity);
        const unit = String((ingredient as any).unit ?? '').trim();
        const name = String((ingredient as any).name ?? '').trim();
        const qtyText = Number.isFinite(qty) && qty > 0 ? `${formatMacro(qty)}` : '';
        return { quantity: `${qtyText}${qtyText && unit ? ' ' : ''}${unit}`.trim(), name: formatIngredientDisplayName(name) };
      })
    : (recipe.ingredientLines ?? []).map(line => {
        const parts = line.split(' ');
        const quantity = parts.slice(0, 2).join(' ');
        const name = parts.slice(2).join(' ') || parts.slice(1).join(' ');
        return { quantity, name: formatIngredientDisplayName(name) };
      });

  const cookingMethodLabel = recipe.cookingMethod
    ? (COOKING_METHOD_OPTIONS.find((option) => option.value === recipe.cookingMethod)?.name
      ?? recipe.cookingMethod.replace(/_/g, ' '))
    : 'Not specified';
    
  const displayImage = (recipe as any).image?.trim() || recipe.recipeImageUrl?.trim() || DEFAULT_RECIPE_IMAGE;
  const r2 = (x: unknown) => Math.floor((Number(x) || 0) * 100) / 100;

  // Nutrition values
  const cGrams = r2(recipe.carbsPerServing);
  const pGrams = r2(recipe.proteinPerServing);
  const fGrams = r2(recipe.fatPerServing);
  const alcoholGrams = r2(
    recipe.alcoholPerServing
    ?? recipe.nutritionPerServing?.alcohol
    ?? 0,
  );
  const sugarGrams = r2(recipe.sugarPerServing);
  const saturatedFatGrams = r2(recipe.saturatedFatPerServing);

  const cKcal = cGrams * 4;
  const pKcal = pGrams * 4;
  const fKcal = fGrams * 9;
  const alcoholKcal = alcoholGrams * 7;
  const sugarKcal = sugarGrams * 4;

  /** Energy distribution bar uses only carb / protein / fat / alcohol (4, 4, 9, 7 kcal per g). */
  const totalKcalMacro = cKcal + pKcal + fKcal + alcoholKcal;

  const cPct = totalKcalMacro > 0 ? (cKcal / totalKcalMacro) * 100 : 0;
  const pPct = totalKcalMacro > 0 ? (pKcal / totalKcalMacro) * 100 : 0;
  const fPct = totalKcalMacro > 0 ? (fKcal / totalKcalMacro) * 100 : 0;
  const alcoholPct = totalKcalMacro > 0 ? (alcoholKcal / totalKcalMacro) * 100 : 0;
  /** Sugar % uses the same denominator as the four macros above (sugar kcal = g × 4). */
  const sugarPct = totalKcalMacro > 0 ? (sugarKcal / totalKcalMacro) * 100 : 0;
  /** Saturated fat kcal = g × 9; % of same total as carb + protein + total fat + alcohol. */
  const saturatedFatKcal = saturatedFatGrams * 9;
  const saturatedFatPct = totalKcalMacro > 0 ? (saturatedFatKcal / totalKcalMacro) * 100 : 0;

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '22px',
          border: '1px solid #BDE7F5',
          bgcolor: '#FFFFFF',
        }}
      >
        <div className="bg-white min-h-full">
          <div className="h-full w-full px-8 py-8">
            
            {/* Breadcrumb */}
            <div className="mb-6 flex w-full min-w-0 items-center gap-2 rounded-lg border border-[#0000001A] px-3 py-2 text-[13px] text-slate-400 ">
              <span >Recipes</span>
              <span>/</span>
              <span className="truncate text-slate-800 font-medium">{recipe.recipeName}</span>
            </div>

            {/* Header Actions */}
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <Link to="/recipes" className="flex items-center gap-2 text-[14px] font-bold text-slate-900 hover:text-black transition-colors">
                <ArrowLeft className="h-4 w-4" />
                Back to Recipes
              </Link>
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap lg:w-auto lg:flex-nowrap lg:items-center lg:gap-2.5">
                {canEditRecipe && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-[36px] w-full gap-2 rounded-lg border-slate-200 text-center text-[14px] font-medium leading-5 tracking-normal text-slate-700 hover:bg-slate-50 sm:w-auto"
                    style={{ fontFamily: 'Inter' }}
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                )}
                {canEditRecipe ? (
                  <div className={`grid w-full grid-cols-1 gap-3 sm:gap-3 lg:w-auto lg:gap-3 ${canCopyRecipe ? 'sm:grid-cols-2' : ''}`}>
                    {canCopyRecipe && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center gap-2 h-[36px] rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium text-[14px] leading-5 tracking-normal text-center"
                        style={{ fontFamily: 'Inter' }}
                        onClick={handleCopy}
                        disabled={isCopying}
                      >
                        <Copy className="h-4 w-4" /> Duplicate
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-center gap-2 h-[36px] rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium text-[14px] leading-5 tracking-normal text-center"
                      style={{ fontFamily: 'Inter' }}
                      onClick={() => navigate(`/recipes/${id}/edit`)}
                    >
                      <Edit className="h-4 w-4" /> Edit Recipe
                    </Button>
                  </div>
                ) : canCopyRecipe ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-[36px] w-full gap-2 rounded-lg border-slate-200 text-center text-[14px] font-medium leading-5 tracking-normal text-slate-700 hover:bg-slate-50 sm:w-auto"
                    style={{ fontFamily: 'Inter' }}
                    onClick={handleCopy}
                    disabled={isCopying}
                  >
                    <Copy className="h-4 w-4" /> Duplicate
                  </Button>
                ) : null}
                {/* <Button size="sm" className="h-[36px] w-full gap-2 rounded-lg bg-[#2EB4E4] px-4 font-bold text-white hover:bg-[#20AEDC] sm:w-auto" onClick={handleAddToMealPlan}>
                  <Plus className="h-4 w-4" /> Add to Meal Plan
                </Button> */}
              </div>
            </div>

            {!canCopyRecipe && (
              <div className="mb-8 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="note">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{copyUnavailableReason}</span>
              </div>
            )}

            {/* Main Info */}
            <div className="mb-8 grid grid-cols-1 gap-6 rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6 xl:grid-cols-[minmax(0,0.67fr)_minmax(0,1fr)] 2xl:grid-cols-[minmax(320px,500px)_minmax(0,1fr)]">
              <div className="min-w-0 shrink-0">
                <img src={displayImage} alt={recipe.recipeName} className="h-[240px] w-full rounded-[18px] object-cover sm:h-[300px] sm:rounded-[20px] lg:h-[310px] lg:rounded-[24px] 2xl:h-[350px]" />
              </div>
              <div className="flex min-w-0 flex-col gap-4 px-1 sm:px-6">
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <h1
                    className="min-w-0 break-words text-[24px] font-medium leading-[30px] tracking-normal text-slate-900 sm:text-[28px] sm:leading-[34px] lg:text-[30px] lg:leading-[36px]"
                    style={{ fontFamily: 'Inter' }}
                  >
                    {recipe.recipeName}
                  </h1>
                  <div className="flex shrink-0 items-center gap-3 pt-1 sm:gap-4 sm:pt-2">
                    <Badge className="bg-[#2EB4E4] hover:bg-[#2EB4E4] text-white rounded-full px-3 py-1 font-semibold text-xs border-0">
                      {recipe.isPublic ? 'Public' : 'Private'}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <h3
                    className="mb-2 text-[16px] font-semibold tracking-normal text-slate-900 sm:text-[18px]"
                    style={{ fontFamily: 'Inter' }}
                  >
                    Description:
                  </h3>
                  <p
                    className="break-words text-[13px] font-normal leading-5 tracking-normal text-slate-600 sm:text-[14px]"
                    style={{ fontFamily: 'Inter' }}
                  >
                    {recipe.description?.trim() || 'No description available.'}
                  </p>
                </div>

                <div className="mt-1 w-full min-w-0 rounded-[14px] border border-[#0000001A] p-4 sm:p-5">
                  <div className="flex items-center gap-2">
                    <img src={cup} alt="Tags" className="h-[24px] w-[24px]" />
                    <h3
                      className="text-[13px] font-medium tracking-normal text-slate-900 sm:text-[14px]"
                      style={{ fontFamily: 'Inter' }}
                    >
                      Tags
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4 pt-4 sm:grid-cols-2">
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">DIET LABELS</p>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {(recipe.dietLabels || []).length > 0 ? (
                          <span className="break-words text-[13px] text-slate-700">
                            {(recipe.dietLabels || [])
                              .map((label) => getDietaryDisplayName(label, 'dietLabels'))
                              .join(', ')}
                          </span>
                        ) : <span className="text-[13px] text-slate-400">—</span>}
                      </div>
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">ALLERGIES</p>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {(recipe.allergens || []).length > 0 ? (
                          recipe.allergens.map((label, i) => (
                            <Tag
                              key={i}
                              variant="danger"
                              size="sm"
                              maxWidth="100%"
                              startIcon={<Ban className="h-3 w-3" />}
                              className="font-medium shadow-none"
                            >
                              {getDietaryDisplayName(label, 'allergies')}
                            </Tag>
                          ))
                        ) : <span className="text-[13px] text-slate-400">—</span>}
                      </div>
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">INTOLERANCE</p>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {(recipe.intoleranceTags || []).length > 0 ? (
                          recipe.intoleranceTags.map((label, i) => (
                            <Tag
                              key={i}
                              variant="warning"
                              size="sm"
                              maxWidth="100%"
                              startIcon={<AlertTriangle className="h-3 w-3" />}
                              className="font-medium text-amber-600 shadow-none"
                            >
                              {getDietaryDisplayName(label, 'intoleranceTags')}
                            </Tag>
                          ))
                        ) : <span className="text-[13px] text-slate-400">—</span>}
                      </div>
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">CUISINE TYPES</p>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {(recipe.cuisines || []).length > 0 ? (
                          <span className="break-words text-[13px] text-slate-700">
                            {(recipe.cuisines || [])
                              .map((label) => getDietaryDisplayName(label, 'cuisineTypes'))
                              .join(', ')}
                          </span>
                        ) : <span className="text-[13px] text-slate-400">—</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <div className="mb-8 rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6 lg:p-8">
              <div className="mb-4 flex items-center gap-2">
                <img src={cup} alt="Tags" className="h-[24px] w-[24px]" />
                <h3
                  className="text-[20px] font-medium text-slate-900"
                  style={{ fontFamily: 'Inter' }}
                >
                  Quick Info
                </h3>
              </div>
              
              <div className="mb-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-4">
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">TIME</p>
                  <p className="text-[15px] text-slate-800 font-medium">{formatTime(recipe.totalTimeMinutes || 0)}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">COOKING METHOD</p>
                  <p className="text-[15px] text-slate-800 font-medium">{cookingMethodLabel}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">SERVINGS</p>
                  <p className="text-[15px] text-slate-800 font-medium">{recipe.servings}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">INGREDIENTS</p>
                  <p className="text-[15px] text-slate-800 font-medium">{ingredientsData.length}</p>
                </div>
              </div>
              
              <div className="grid gap-x-6 gap-y-4 grid-cols-4">
                <div className="flex flex-col gap-3">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">DEFAULT SERVING</p>
                  <p className="text-[14px] text-slate-800 font-medium">
                    {formatServingSizeLabel(recipe.servingSizes?.find(s => s.isDefault) || recipe.servingSizes?.[0])}
                  </p>
                </div>
                <div className="flex flex-col gap-3 w-full">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">AVAILABLE SERVING SIZES</p>
                  <div className="flex gap-2">
                    {(recipe.servingSizes || []).length > 0 ? recipe.servingSizes.map(s => (
                      <Badge key={s.servingId} variant={s.isDefault ? "default" : "outline"} className={s.isDefault ? "inline-flex w-fit whitespace-nowrap bg-[#2EB4E4] hover:bg-[#20AEDC] text-white border-0 rounded-full px-4 py-1 font-medium shadow-none" : "inline-flex w-fit whitespace-nowrap border-slate-200 text-slate-600 rounded-full px-4 py-1 font-medium shadow-none"}>
                        {formatServingSizeLabel(s)}
                      </Badge>
                    )) : (
                      <>
                        <Badge className="inline-flex w-fit whitespace-nowrap bg-[#2EB4E4] hover:bg-[#20AEDC] text-white border-0 rounded-full px-4 py-1 font-medium shadow-none">Bowl(350g)</Badge>
                        <Badge variant="outline" className="inline-flex w-fit whitespace-nowrap border-slate-200 text-slate-600 rounded-full px-4 py-1 font-medium shadow-none">Cup(240g)</Badge>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Ingredients */}
            <div className="mb-8 rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6 lg:p-8">
              <h3
                className="mb-2 sm:mb-3 md:mb-4 xl:mb-4 2xl:mb-5 text-[20px] font-semibold text-slate-900"
                style={{ fontFamily: 'Inter' }}
              >
                Ingredients:
              </h3>
              <RecipeIngredientCards items={ingredientsData} />
            </div>

            {/* Instructions & Tips */}
            <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
              <div className="rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <ChefHat className="h-5 w-5 text-slate-800" />
                  <h3 className="text-[16px] font-bold text-slate-900 sm:text-[18px]">Instructions:</h3>
                </div>
                {recipe.cookingInstructions?.length > 0 ? (
                  <ul >
                    {recipe.cookingInstructions.map((step, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-2"></span>
                        <span
                          className="text-[14px] font-normal leading-relaxed text-[#364153]"
                          style={{ fontFamily: 'Inter' }}
                        >
                          {step.replace(/^- /, '')}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p
                    className="text-[14px] font-normal text-[#364153]"
                    style={{ fontFamily: 'Inter' }}
                  >
                    No cooking instructions added.
                  </p>
                )}
              </div>

              <div className="rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6 lg:p-8">
                <div className="flex items-center gap-2 mb-6">
                <ChefHat className="h-5 w-5 text-slate-800" />
                  <h3 className="text-[16px] font-bold text-slate-900 sm:text-[18px]">Cooking Tips:</h3>
                </div>
                {recipe.cookingTips?.trim().length ? (
                  <ul className="space-y-3">
                    {recipe.cookingTips.split('\n').filter(tip => tip.trim().length > 0).map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-2"></span>
                        <span
                          className="text-[14px] font-normal leading-relaxed text-[#364153]"
                          style={{ fontFamily: 'Inter' }}
                        >
                          {tip.replace(/^- /, '')}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p
                    className="text-[14px] font-normal text-[#364153]"
                    style={{ fontFamily: 'Inter' }}
                  >
                    No cooking tips added.
                  </p>
                )}
              </div>
            </div>

            {/* Nutrition */}
            <div className="mb-8 rounded-[20px] border border-[#0000001A] p-4 shadow-sm sm:p-6 lg:p-8">
              <h3
                className="mb-[8.5px] text-[18px] font-bold text-slate-900"
                style={{ fontFamily: 'Inter' }}
              >
                Nutrition(per serving)
              </h3>
              <p
                className="mb-[8.5px] text-[12px] font-normal text-slate-400"
                style={{ fontFamily: 'Inter' }}
              >
                Current intake breakdown
              </p>
              
              <div className="mb-3 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[12px] font-medium text-slate-500">Energy Distribution (% kcal)</p>
                <p className="text-[12px] font-medium text-slate-500">Total: ~{formatMacro((recipe as any).totalCalories || recipe.caloriesPerServing || 0)} kcal</p>
              </div>

              <TooltipProvider>
                <div className="flex h-[32px] w-full rounded-md overflow-hidden mb-2">
                  {Number(cPct.toFixed(2)) > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div style={{ width: `${cPct}%`, backgroundColor: '#60C6D9' }} className="flex items-center justify-center text-white text-xs font-bold border-r border-white border-opacity-20 min-w-fit px-1 hover:brightness-110 transition-all cursor-default">{Number(cPct.toFixed(2))}%</div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Carbohydrates: {Number(cPct.toFixed(2))}%</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {Number(pPct.toFixed(2)) > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div style={{ width: `${pPct}%`, backgroundColor: '#3B82F6' }} className="flex items-center justify-center text-white text-xs font-bold border-r border-white border-opacity-20 min-w-fit px-1 hover:brightness-110 transition-all cursor-default">{Number(pPct.toFixed(2))}%</div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Protein: {Number(pPct.toFixed(2))}%</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {Number(fPct.toFixed(2)) > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div style={{ width: `${fPct}%`, backgroundColor: '#6366F1' }} className="flex items-center justify-center text-white text-xs font-bold border-r border-white border-opacity-20 min-w-fit px-1 hover:brightness-110 transition-all cursor-default">{Number(fPct.toFixed(2))}%</div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Fat: {Number(fPct.toFixed(2))}%</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {Number(alcoholPct.toFixed(2)) > 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div style={{ width: `${alcoholPct}%`, backgroundColor: MACRO_CHART_COLORS.alcohol }} className="flex items-center justify-center text-white text-xs font-bold border-r border-white border-opacity-20 min-w-fit px-1 hover:brightness-110 transition-all cursor-default">{Number(alcoholPct.toFixed(2))}%</div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Alcohol: {Number(alcoholPct.toFixed(2))}%</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {(Number(cPct.toFixed(2)) === 0 && Number(pPct.toFixed(2)) === 0 && Number(fPct.toFixed(2)) === 0 && Number(alcoholPct.toFixed(2)) === 0) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div style={{ width: '100%', backgroundColor: '#E2E8F0' }} className="flex items-center justify-center text-slate-500 text-xs font-bold min-w-fit px-1 cursor-default">0%</div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>No macro data available</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TooltipProvider>

              <div className="flex items-center gap-6 mb-3 flex-wrap">
                {Number(cPct.toFixed(2)) > 0 && <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#60C6D9]"></div><span className="text-xs font-medium text-slate-500">Carbohydrates</span></div>}
                {Number(pPct.toFixed(2)) > 0 && <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#3B82F6]"></div><span className="text-xs font-medium text-slate-500">Protein</span></div>}
                {Number(fPct.toFixed(2)) > 0 && <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#6366F1]"></div><span className="text-xs font-medium text-slate-500">Fat</span></div>}
                {Number(alcoholPct.toFixed(2)) > 0 && <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: MACRO_CHART_COLORS.alcohol }}></div><span className="text-xs font-medium text-slate-500">Alcohol</span></div>}
              </div>

              <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4 2xl:gap-6">
                {[
                  { label: 'Carbohydrates', value: cGrams, percentage: cPct, color: '#60C6D9' },
                  { label: 'Protein', value: pGrams, percentage: pPct, color: '#3B82F6' },
                  { label: 'Sugar', value: sugarGrams, percentage: sugarPct, color: '#EF4444' },
                  { label: 'Alcohol', value: alcoholGrams, percentage: alcoholPct, color: MACRO_CHART_COLORS.alcohol },
                ].map((item) => (
                  <NutritionMetricStatusCard
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    percentage={item.percentage}
                    color={item.color}
                  />
                ))}
              </div>

              {/* Total Fat Full Width Row */}
              <div className="border border-[#0000001A] rounded-[16px] p-5 flex flex-col lg:flex-row lg:items-center gap-6">
                <div className="flex items-center gap-5 min-w-[250px]">
                  <CircularProgress percentage={Math.min(Number(fPct.toFixed(2)), 100)} color="#6366F1" size={54} strokeWidth={5} />
                  <div>
                    <p className="text-[13px] font-bold text-slate-900 mb-1">Total Fat</p>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[12px] text-slate-400">{fGrams}g</span>
                      <span className="text-[12px] font-bold text-slate-700">{Number(fPct.toFixed(2))}%</span>
                    </div>
                  </div>
                </div>
                
                {/* Saturated fat: kcal = g × 9; % vs same totalKcalMacro as macros above */}
                <div className="flex flex-wrap items-start gap-4 lg:gap-6">
                  {saturatedFatGrams > 0 && (
                    <div className="min-w-0 max-w-sm">
                      <NutritionMetricStatusCard
                        label="Saturated Fat"
                        value={saturatedFatGrams}
                        percentage={saturatedFatPct}
                        color="#7C3AED"
                        hideBorder
                      />
                    </div>
                  )}
                </div>
              </div>

            </div>

          </div>
        </div>
      </Paper>
    </Box>
  );
};

export default RecipeDetailsPage;
